const chapter = {
  id: 'amines',
  slug: 'amines',
  subject: 'chemistry',
  subjectLabel: 'Chemistry',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Amines & Diazonium Salts',
        hindi: 'ऐमीन और डाइऐजोनियम लवण',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '2 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 2 प्रश्न',
  },
  syllabusTags: ['Organic Chemistry', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'classification',
      title: {
        english: 'Classification & Structure',
        hindi: 'वर्गीकरण और संरचना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Replacement of H of NH₃ by R → 1° (RNH₂), 2° (R₂NH), 3° (R₃N), and 4° quaternary salt $R_4N^+X^-$.',
        hindi: 'NH₃ के H का R → 1° (RNH₂), 2° (R₂NH), 3° (R₃N), और 4° चतुर्धातुक नमक $R_4N^+X^-$ द्वारा प्रतिस्थापन।',
            },
            {
              english: 'Geometry: $sp^3$ N, pyramidal; one lone pair. ∠HNC ≈ 108°.',
        hindi: 'ज्यामिति: $sp^3$ N, पिरामिडनुमा; एक अकेला जोड़ा. ∠HNC ≈ 108°.',
            },
            {
              english: 'Aniline: lone pair partly delocalised into ring ⇒ less basic, planar at N.',
        hindi: 'एनिलीन: अकेला जोड़ा आंशिक रूप से वलय में स्थानांतरित हो गया ⇒ कम क्षारीय, N पर तलीय।',
            },
            {
              english: 'BP order: 1° > 2° > 3° (H-bonding capability falls; tertiary cannot H-bond).',
        hindi: 'बीपी क्रम: 1° > 2° > 3° (एच-बॉन्डिंग क्षमता गिरती है; तृतीयक एच-बॉन्ड नहीं कर सकता)।',
            },
            {
              english: 'Solubility in water $\\downarrow$ with chain length; aromatic amines less soluble.',
        hindi: 'श्रृंखला की लंबाई के साथ पानी में घुलनशीलता $\\downarrow$; ऐरोमैटिक ऐमीन कम घुलनशील होते हैं। **हेलोऐल्केन (हॉफमैन अमोनोलिसिस) से**: $\\downarrow$। मिश्रण (अति-क्षारीकरण समस्या)।',
            },
          ],
        },
      ],
    },
    {
      id: 'preparation',
      title: {
        english: 'Preparation',
        hindi: 'तैयारी',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**From haloalkanes (Hofmann ammonolysis)**: $R\\text{-X} + NH_3 \\to R\\text{NH}_2 \\to R_2NH \\to R_3N \\to R_4N^+X^-$. Mixture (over-alkylation problem).',
        hindi: '**हैलोअल्केन्स (हॉफमैन अमोनोलिसिस) से**: $R\\text{-X} + NH_3 \\to R\\text{NH}_2 \\to R_2NH \\to R_3N \\to R_4N^+X^-$। मिश्रण (अति-क्षारीकरण समस्या)।',
            },
            {
              english: '**Gabriel phthalimide synthesis** — gives **pure 1° aliphatic amine**: phthalimide + KOH → K-salt; + R-X → N-alkylphthalimide; hydrolysis (acid or alkali) or hydrazinolysis → R-NH₂. **Cannot be used for aryl amines** (aryl halides do not undergo SN with K-phthalimide).',
        hindi: '**गेब्रियल फ़ेथलीमाइड संश्लेषण** - **शुद्ध 1° एलिफैटिक अमाइन** देता है: फ़ेथलीमाइड + KOH → K-नमक; + आर-एक्स → एन-एल्काइलफथालिमाइड; हाइड्रोलिसिस (अम्ल या क्षार) या हाइड्रेज़िनोलिसिस → आर-एनएच₂। **एरिल एमाइन के लिए इस्तेमाल नहीं किया जा सकता** (एरिल हैलाइड्स के-फथैलिमाइड के साथ एसएन से नहीं गुजरते हैं)।',
            },
            {
              english: '**Reduction**:',
        hindi: '**कमी**:',
            },
            {
              english: '• $RCN + H_2/Ni$ or $LiAlH_4$ → $RCH_2NH_2$ (1°, one C added).',
        hindi: '• $RCN + H_2/Ni$ या $LiAlH_4$ → $RCH_2NH_2$ (1°, एक C जोड़ा गया)।',
            },
            {
              english: '• $RNO_2 + Sn/HCl$ or $H_2/Ni$ → RNH₂.',
        hindi: '• $RNO_2 + Sn/HCl$ या $H_2/Ni$ → RNH₂.',
            },
            {
              english: '• $RCONH_2 + LiAlH_4$ → RCH₂NH₂ (1°, same C count).',
        hindi: '• $RCONH_2 + LiAlH_4$ → RCH₂NH₂ (1°, समान C गिनती)।',
            },
            {
              english: '**Hofmann bromamide degradation** of amide: $RCONH_2 + Br_2 + 4NaOH \\to RNH_2 + Na_2CO_3 + 2NaBr + 2H_2O$ (one C **lost**, gives 1° amine).',
        hindi: '**हॉफमैन ब्रोमामाइड क्षरण** एमाइड का: $RCONH_2 + Br_2 + 4NaOH \\to RNH_2 + Na_2CO_3 + 2NaBr + 2H_2O$ (एक C **खोया**, 1° एमाइन देता है)।',
            },
            {
              english: '**Aniline**: nitrobenzene + Sn/HCl or Fe/HCl (cheaper); also $H_2/Ni$.',
        hindi: '**एनिलीन**: नाइट्रोबेंजीन + Sn/HCl या Fe/HCl (सस्ता); साथ ही $H_2/Ni$.',
            },
          ],
        },
      ],
    },
    {
      id: 'basicity',
      title: {
        english: 'Basicity Trends',
        hindi: 'क्षारीयता के रुझान',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Gas phase (no solvent): $3° > 2° > 1° > NH_3$ (purely +I effect).',
        hindi: 'गैस चरण (कोई विलायक नहीं): $3° > 2° > 1° > NH_3$ (विशुद्ध रूप से +I प्रभाव)।',
            },
            {
              english: 'In **water**: solvation (H-bond stabilisation of ammonium) becomes important; trends are non-monotonic.',
        hindi: '**पानी** में: सॉल्वेशन (अमोनियम का एच-बॉन्ड स्थिरीकरण) महत्वपूर्ण हो जाता है; रुझान गैर-मोनोटोनिक हैं।',
            },
            {
              english: '• Aliphatic amines (alkyl = $CH_3$): $(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$.',
        hindi: '• एलिफैटिक एमाइन (एल्काइल = $CH_3$): $(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$.',
            },
            {
              english: '• Aliphatic amines (alkyl = $C_2H_5$): $(C_2H_5)_2NH > (C_2H_5)_3N > C_2H_5NH_2 > NH_3$ (slightly different due to bulkier ethyl).',
        hindi: '• एलिफैटिक एमाइन (एल्काइल = $C_2H_5$): $(C_2H_5)_2NH > (C_2H_5)_3N > C_2H_5NH_2 > NH_3$ (भारी एथिल के कारण थोड़ा अलग)।',
            },
            {
              english: '**Aniline**: $K_b = 4.27\\times 10^{-10}$, much weaker than $NH_3$ (lone pair on N delocalised into ring).',
        hindi: '**एनिलिन**: $K_b = 4.27\\times 10^{-10}$, $NH_3$ से बहुत कमजोर (एन पर अकेला जोड़ा रिंग में स्थानीयकृत)।',
            },
            {
              english: 'Substituent effect on aryl amines: EDG (CH₃, OCH₃) increase basicity; EWG (NO₂, CN) decrease (especially at p-).',
        hindi: 'एरिल एमाइन पर स्थानापन्न प्रभाव: EDG (CH₃, OCH₃) मौलिकता बढ़ाता है; EWG (NO₂, CN) में कमी (विशेषकर p- पर)।',
            },
            {
              english: 'Order: $p\\text{-toluidine} > \\text{aniline} > p\\text{-nitroaniline}$. Order: $p\\text{-NO}_2 > m > o$ for nitroanilines (steric/H-bond).',
        hindi: 'आदेश: $p\\text{-toluidine} > \\text{aniline} > p\\text{-nitroaniline}$. आदेश: नाइट्रोएनिलीन (स्टेरिक/एच-बॉन्ड) के लिए $p\\text{-NO}_2 > m > o$।',
            },
          ],
        },
      ],
    },
    {
      id: 'reactions-amines',
      title: {
        english: 'Reactions of Amines',
        hindi: 'ऐमीन की अभिक्रियाएँ',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Acylation** with RCOCl / (RCO)₂O → amide (1° and 2° only; 3° has no N–H). Pyridine added to absorb HCl.',
        hindi: '**एसाइलेशन** RCOCl / (RCO)₂O → एमाइड (केवल 1° और 2°; 3° में कोई N-H नहीं है) के साथ। एचसीएल को अवशोषित करने के लिए पाइरीडीन मिलाया गया।',
            },
            {
              english: '**Carbylamine test (1° only)**: $RNH_2 + CHCl_3 + 3KOH \\xrightarrow{\\Delta} R\\text{-N=C} + 3KCl + 3H_2O$ — foul smell of isocyanide.',
        hindi: '**कार्बिलामाइन परीक्षण (केवल 1°)**: $RNH_2 + CHCl_3 + 3KOH \\xrightarrow{\\Delta} R\\text{-N=C} + 3KCl + 3H_2O$ — आइसोसायनाइड की दुर्गंध।',
            },
            {
              english: '**Hinsberg test** with PhSO₂Cl in NaOH:',
        hindi: '**हिंसबर्ग परीक्षण** NaOH में PhSO₂Cl के साथ:',
            },
            {
              english: '• 1° amine → soluble in NaOH (N–H acidic next to SO₂).',
        hindi: '• 1° अमीन → NaOH में घुलनशील (SO₂ के आगे N-H अम्लीय)।',
            },
            {
              english: '• 2° amine → insoluble in NaOH (no acidic H on N).',
        hindi: '• 2° अमीन → NaOH में अघुलनशील (N पर कोई अम्लीय H नहीं)।',
            },
            {
              english: '• 3° amine → no reaction.',
        hindi: '• 3° अमीन → कोई प्रतिक्रिया नहीं। **HNO₂ (NaNO₂ + dil HCl, 273-278 K) के साथ प्रतिक्रिया**:',
            },
            {
              english: '**Reaction with HNO₂ (NaNO₂ + dil HCl, 273-278 K)**:',
        hindi: '**HNO₂ (NaNO₂ + तनु HCl, 273-278 K) के साथ प्रतिक्रिया**:',
            },
            {
              english: '• 1° aliphatic → R-OH + N₂ (effervescence).',
        hindi: '• 1° स्निग्ध → R-OH + N₂ (उत्तेजना)।',
            },
            {
              english: '• 2° → N-nitrosamine (yellow oily, R₂N-N=O — carcinogenic).',
        hindi: '• 2° → एन-नाइट्रोसामाइन (पीला तैलीय, R₂N-N=O — carcinogenic)।',
            },
            {
              english: '• 3° aliphatic → no visible reaction (forms salt).',
        hindi: '• 3° स्निग्ध → कोई दृश्य प्रतिक्रिया नहीं (नमक बनता है)।',
            },
            {
              english: '• 1° aromatic → **diazonium salt** $ArN_2^+Cl^-$ (stable in cold).',
        hindi: '• 1° सुगंधित → **डाइज़ोनियम नमक** $ArN_2^+Cl^-$ (ठंड में स्थिर)।',
            },
            {
              english: 'Reactions of aniline:',
        hindi: 'एनिलिन की प्रतिक्रियाएं:',
            },
            {
              english: '• Acetylation deactivates (–NHCOCH₃ less ring-activating) — used to control over-bromination so monosubstitution possible.',
        hindi: '• एसिटिलेशन निष्क्रिय करता है (-NHCOCH₃ कम रिंग-सक्रियण) - ओवर-ब्रोमिनेशन को नियंत्रित करने के लिए उपयोग किया जाता है ताकि मोनोप्रतिस्थापन संभव हो।',
            },
            {
              english: '• Direct nitration of aniline gives mixture incl. *m*-nitroaniline (in acid, $-NH_3^+$ is meta directing). Acetylate first → nitrate → hydrolyse → p-nitroaniline.',
        hindi: '• एनिलिन का सीधा नाइट्रेशन मिश्रण सहित देता है। *m*-नाइट्रोएनिलिन (एसिड में, $-NH_3^+$ मेटा डायरेक्टिंग है)। एसिटिलेट प्रथम → नाइट्रेट → हाइड्रोलाइज़ → पी-नाइट्रोएनिलिन।',
            },
          ],
        },
      ],
    },
    {
      id: 'diazonium',
      title: {
        english: 'Diazonium Salts',
        hindi: 'डाइऐजोनियम लवण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Formed at 0–5 °C: $ArNH_2 + NaNO_2 + 2HCl \\to ArN_2^+Cl^- + NaCl + 2H_2O$.',
        hindi: '0-5 डिग्री सेल्सियस पर बनता है: $ArNH_2 + NaNO_2 + 2HCl \\to ArN_2^+Cl^- + NaCl + 2H_2O$।',
            },
            {
              english: 'Aromatic diazonium ions are stabilised by resonance into ring.',
        hindi: 'ऐरोमैटिक डायज़ोनियम आयन रिंग में अनुनाद द्वारा स्थिर होते हैं। **सैंडमेयर प्रतिक्रियाएं** ( का प्रतिस्थापन):',
            },
            {
              english: '**Sandmeyer reactions** (replacement of $-N_2^+$):',
        hindi: '**सैंडमेयर प्रतिक्रियाएं** ($-N_2^+$ का प्रतिस्थापन):',
            },
            {
              english: '• $+ CuCl/HCl$ → ArCl;  $+ CuBr/HBr$ → ArBr;  $+ CuCN/KCN$ → ArCN.',
        hindi: '• $+ CuCl/HCl$ → ArCl; $+ CuBr/HBr$ → ArBr; $+ CuCN/KCN$ → ArCN.',
            },
            {
              english: 'Gattermann variant uses $Cu/HX$ — same product.',
        hindi: 'गैटरमैन संस्करण $Cu/HX$ - समान उत्पाद का उपयोग करता है।',
            },
            {
              english: '$+ KI$ → ArI (no Cu needed); $+ HBF_4 \\to ArN_2^+BF_4^- \\xrightarrow{\\Delta} ArF$ (Balz-Schiemann).',
        hindi: '$+ KI$ → ArI (कोई Cu आवश्यक नहीं); $+ HBF_4 \\to ArN_2^+BF_4^- \\xrightarrow{\\Delta} ArF$ (बाल्ज़-शिमैन)।',
            },
            {
              english: '$+ H_2O,\\Delta$ → ArOH (phenol);  $+ H_3PO_2$ or $C_2H_5OH$ → ArH (deamination).',
        hindi: '$+ H_2O,\\Delta$ → ArOH (फिनोल); $+ H_3PO_2$ या $C_2H_5OH$ → ArH (डीमिनेशन)। **युग्मन प्रतिक्रियाएं** (सक्रिय एरीन पर इलेक्ट्रोफिलिक सुगंधित प्रतिस्थापन):',
            },
            {
              english: '**Coupling reactions** (electrophilic aromatic substitution at activated arene):',
        hindi: '**युग्मन प्रतिक्रियाएं** (सक्रिय एरीन पर इलेक्ट्रोफिलिक सुगंधित प्रतिस्थापन):',
            },
            {
              english: '• With phenol in mildly alkaline → p-hydroxyazobenzene (orange dye).',
        hindi: '• हल्के क्षारीय फिनोल के साथ → पी-हाइड्रॉक्सीएज़ोबेंजीन (नारंगी डाई)।',
            },
            {
              english: '• With aniline in mildly acidic → p-aminoazobenzene (yellow dye).',
        hindi: '• हल्के अम्लीय में एनिलिन के साथ → पी-एमिनोएज़ोबेंजीन (पीली डाई)।',
            },
            {
              english: '• Used for synthesis of azo dyes (methyl orange = sulphanilic acid + N,N-dimethylaniline).',
        hindi: '• एज़ो रंगों (मिथाइल orange = sulphanilic acid + N,N-डाइमिथाइलनिलिन) के संश्लेषण के लिए उपयोग किया जाता है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'RCONH_2 + Br_2 + 4NaOH \\to RNH_2 + Na_2CO_3 + 2NaBr + 2H_2O',
      desc: {
        english: 'Hofmann degradation',
        hindi: 'हॉफमैन गिरावट',
      },
    },
    {
      latex: 'RCN + 4[H] \\xrightarrow{LiAlH_4} RCH_2NH_2',
      desc: {
        english: 'Nitrile reduction',
        hindi: 'नाइट्राइल कमी',
      },
    },
    {
      latex: 'ArNH_2 + HNO_2 + HCl \\xrightarrow{0\\text{–}5°C} ArN_2^+Cl^-',
      desc: {
        english: 'Diazotisation',
        hindi: 'डायज़ोटाइजेशन',
      },
    },
    {
      latex: 'ArN_2^+ + CuCN \\to ArCN + N_2',
      desc: {
        english: 'Sandmeyer (CN)',
        hindi: 'सैंडमेयर (CN)',
      },
    },
    {
      latex: 'ArN_2^+ + HBF_4 \\xrightarrow{\\Delta} ArF',
      desc: {
        english: 'Balz-Schiemann',
        hindi: 'बाल्ज़-शिमैन',
      },
    },
    {
      latex: 'RNH_2 + CHCl_3 + 3KOH \\to RNC + 3KCl + 3H_2O',
      desc: {
        english: 'Carbylamine test',
        hindi: 'कार्बिलामाइन परीक्षण',
      },
    },
    {
      latex: '(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3',
      desc: {
        english: 'Basicity in water (Me)',
        hindi: 'पानी में मौलिकता (मैं)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Test',
      problem: {
        english: 'Carbylamine test is positive for:',
        hindi: 'कार्बिलामाइन परीक्षण इसके लिए सकारात्मक है:',
      },
      answer: {
        english: '1° amines only',
        hindi: 'केवल 1° एमाइन',
      },
      solution: {
        english: 'Foul-smelling isocyanide.',
        hindi: 'दुर्गंधयुक्त आइसोसायनाइड।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Basicity',
      problem: {
        english: 'Order of basicity in water (methylamines):',
        hindi: 'पानी में क्षारकता का क्रम (मिथाइलमाइन्स):',
      },
      answer: {
        english: '$(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$',
        hindi: '$(CH_3)_2NH > CH_3NH_2 > (CH_3)_3N > NH_3$',
      },
      solution: {
        english: 'Solvation + +I balance.',
        hindi: 'समाधान + +I संतुलन।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Synthesis',
      problem: {
        english: 'Best route to pure 1° aliphatic amine:',
        hindi: 'शुद्ध 1° एलिफैटिक एमाइन का सर्वोत्तम मार्ग:',
      },
      answer: {
        english: 'Gabriel phthalimide synthesis',
        hindi: 'गेब्रियल फ्थैलिमाइड संश्लेषण',
      },
      solution: {
        english: 'No over-alkylation; cannot work for ArNH₂.',
        hindi: 'कोई अति-क्षारीकरण नहीं; ArNH₂ के लिए काम नहीं कर सकता। ArN₂⁺ + Cu₂Cl₂ / HCl देता है:',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Diazonium',
      problem: {
        english: 'ArN₂⁺ + Cu₂Cl₂ / HCl gives:',
        hindi: 'ArN₂⁺ + Cu₂Cl₂ / HCl देता है:',
      },
      answer: {
        english: 'ArCl',
        hindi: 'ArCl',
      },
      solution: {
        english: 'Sandmeyer.',
        hindi: 'सैंडमेयर।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Hofmann degradation',
      problem: {
        english: '$CH_3CONH_2 + Br_2 + 4NaOH$ gives:',
        hindi: '$CH_3CONH_2 + Br_2 + 4NaOH$ देता है:',
      },
      answer: {
        english: 'CH₃NH₂ (one C lost)',
        hindi: 'CH₃NH₂ (एक C खो गया)',
      },
      solution: {
        english: 'Hofmann bromamide reaction.',
        hindi: 'हॉफमैन ब्रोमामाइड प्रतिक्रिया।',
      },
    },
  ],
  next: {
    slug: 'biomolecules',
    title: 'Biomolecules',
  },
};

export default chapter;
